import { ref, type Ref } from 'vue'

import type {
  StoryboardWorkspaceState,
} from './types'

interface UploadedMaterial {
  id?: number
  name?: string
  file_url?: string
  updated_at?: string
}

interface FinalDeliveryWorkspaceOptions {
  projectId: number
  finalVideoUploadInput: Ref<HTMLInputElement | null>
  finalVideoUploading: Ref<boolean>
  finalWorkspaceSaving: Ref<boolean>
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: { successMessage?: string; silent?: boolean }) => Promise<void>
  uploadMaterial: (formData: FormData, options: { project_id: number; material_type: string; description: string }) => Promise<UploadedMaterial>
  getActionErrorMessage: (error: any) => string | null
  error: (message: string) => void
}

export function useFinalDeliveryWorkspace(options: FinalDeliveryWorkspaceOptions) {
  const finalVideoPlayerError = ref('')

  function triggerFinalVideoUpload() {
    if (options.finalVideoUploadInput.value) {
      options.finalVideoUploadInput.value.value = ''
      options.finalVideoUploadInput.value.click()
    }
  }

  async function handleFinalVideoFileSelected(event: Event) {
    const target = event.target as HTMLInputElement | null
    const file = target?.files?.[0]
    if (!file) {
      return
    }

    options.finalVideoUploading.value = true
    try {
      const formData = new FormData()
      formData.append('file', file)
      const material = await options.uploadMaterial(formData, {
        project_id: options.projectId,
        material_type: 'video_clip',
        description: 'final-delivery-video',
      })
      const videoUrl = String(material.file_url || '').trim()
      if (!videoUrl) {
        throw new Error('上传成功，但未返回成片地址')
      }

      const current = options.getStoryboardWorkspaceStatePayload()
      await options.persistStoryboardWorkspaceState({
        ...current,
        final_delivery: {
          ...(current.final_delivery || {}),
          preferred_final_video_source: 'uploaded_final',
          uploaded_final_video: {
            url: videoUrl,
            name: material.name,
            material_id: material.id,
            uploaded_at: material.updated_at,
          },
        },
      }, { successMessage: '人工成片已上传' })
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '上传成片失败')
    } finally {
      options.finalVideoUploading.value = false
      if (target) {
        target.value = ''
      }
    }
  }

  async function handleSaveFinalWorkspace() {
    options.finalWorkspaceSaving.value = true
    try {
      const current = options.getStoryboardWorkspaceStatePayload()
      await options.persistStoryboardWorkspaceState({
        ...current,
        final_delivery: {
          ...(current.final_delivery || {}),
          uploaded_final_video: current.final_delivery?.uploaded_final_video || null,
        },
      }, { successMessage: '终版交付设置已保存' })
    } catch (error: any) {
      options.error(options.getActionErrorMessage(error) || '保存终版交付设置失败')
    } finally {
      options.finalWorkspaceSaving.value = false
    }
  }

  function handleDownloadAsset(url: string, filename: string) {
    const targetUrl = String(url || '').trim()
    if (!targetUrl || typeof window === 'undefined') {
      return
    }

    const anchor = document.createElement('a')
    anchor.href = targetUrl
    anchor.download = filename
    anchor.target = '_blank'
    anchor.rel = 'noreferrer'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }

  function handleFinalVideoLoadedData() {
    finalVideoPlayerError.value = ''
  }

  function handleFinalVideoPlaybackError() {
    finalVideoPlayerError.value = '当前浏览器没有正确解码这段成片视频。如果这是旧成片，请重新触发一次成片生成；新的成片会按 Safari 更稳的 H.264/AAC 方式输出。'
  }

  return {
    finalVideoPlayerError,
    handleDownloadAsset,
    handleFinalVideoFileSelected,
    handleFinalVideoLoadedData,
    handleFinalVideoPlaybackError,
    handleSaveFinalWorkspace,
    triggerFinalVideoUpload,
  }
}